import { ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export type SourceRef = string | { text: string; url: string };

interface SourcesProps {
  items: SourceRef[];
  className?: string;
}

/**
 * Collapsible "Sources" block for commercial pages.
 * Matches the references block in GuideLayout. Used to back up cited
 * statistics (NHS, FSRH) so the page reads as authoritative and citable.
 */
const Sources = ({ items, className = "" }: SourcesProps) => {
  if (!items || items.length === 0) return null;

  return (
    <div className={`mt-6 ${className}`}>
      <Collapsible>
        <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
          <CollapsibleTrigger className="flex items-center gap-2 group cursor-pointer px-4 py-3 transition-colors hover:bg-muted/40 w-full">
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">Sources ({items.length})</span>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
            <ol className="space-y-1 text-[11px] text-muted-foreground leading-relaxed list-decimal list-inside px-4 pb-4 pt-1">
              {items.map((ref, i) => (
                <li key={i}>
                  {typeof ref === "string" ? (
                    ref
                  ) : (
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 decoration-muted-foreground/40 hover:text-foreground/60 transition-colors"
                    >
                      {ref.text}
                    </a>
                  )}
                </li>
              ))}
            </ol>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};

export default Sources;
